<template>
    <section class="notice" :class="'notice--' + tone" :role="tone === 'danger' ? 'alert' : 'status'">
        <span v-if="icon" class="notice__icon" aria-hidden="true"><i :class="icon"></i></span>
        <div class="notice__body">
            <h2 v-if="title" class="notice__title">{{ title }}</h2>
            <p v-if="description" class="notice__description">{{ description }}</p>
            <slot></slot>
        </div>
        <div v-if="$slots.actions" class="notice__actions"><slot name="actions"></slot></div>
    </section>
</template>
<script>
/**
 * Aviso dentro de una pantalla: algo que la persona debe atender, sin
 * interrumpirla (no es un diálogo ni un toast).
 *
 *   <NoticeBanner tone="warning" icon="fas fa-cash-register"
 *                 title="Tienes 2 cajas sin cerrar" description="Ciérralas antes de seguir.">
 *       <ul>…detalle…</ul>
 *       <template v-slot:actions><v-btn small outlined to="/…">Ir a cajas</v-btn></template>
 *   </NoticeBanner>
 *
 * Tonos: `warning` (pendiente de atender), `danger` (algo falló o bloquea),
 * `info`, `success`. El color sale de los tokens `--app-*` (tema de Vuetify),
 * nunca de un hex; el texto va en tinta normal para que se lea en cualquier
 * tono. `danger` se anuncia como alerta; los demás como estado.
 *
 * Sin `v-alert`: su API cambia en Vuetify 3 y el aviso es solo HTML y CSS.
 */
export default {
    name: 'NoticeBanner',
    props: {
        tone: {
            type: String,
            default: 'warning',
            validator: (tone) => ['warning', 'danger', 'info', 'success'].includes(tone)
        },
        title: { type: String, default: '' },
        description: { type: String, default: '' },
        // Icono de Font Awesome (`fas fa-cash-register`).
        icon: { type: String, default: '' }
    }
};
</script>
<style scoped>
.notice {
    --notice: var(--app-warning);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    border: 1px solid var(--app-line-strong);
    border-left: 4px solid var(--notice);
    border-radius: 6px;
    background: var(--app-surface);
    color: var(--app-text);
}
.notice--danger { --notice: var(--app-danger); }
.notice--info { --notice: var(--app-info); }
.notice--success { --notice: var(--app-success); }
@supports (color: color-mix(in srgb, red 50%, blue)) {
    .notice {
        border-color: color-mix(in srgb, var(--notice) 35%, transparent);
        border-left-color: var(--notice);
        background: color-mix(in srgb, var(--notice) 10%, var(--app-surface));
    }
}
.notice__icon {
    flex: 0 0 auto;
    margin-top: 2px;
    font-size: 18px;
    /* el color del tono, oscurecido con la tinta: el amarillo puro no se ve sobre su propio fondo */
    color: var(--notice);
}
@supports (color: color-mix(in srgb, red 50%, blue)) {
    .notice__icon {
        color: color-mix(in srgb, var(--notice) 60%, var(--v-onsurface-base, #000));
    }
}
.notice__body {
    flex: 1 1 auto;
    min-width: 0;
}
.notice__title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
}
.notice__description {
    margin: 2px 0 0;
    font-size: 14px;
    color: var(--app-text-muted);
}
.notice__body > :not(h2):not(p) {
    margin-top: 8px;
}
.notice__actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
}
/* celular: el botón baja debajo del texto, a todo el ancho */
@media (max-width: 599px) {
    .notice {
        flex-wrap: wrap;
    }
    .notice__actions {
        flex: 1 1 100%;
    }
    .notice__actions > * {
        flex: 1 1 auto;
    }
}
</style>
